import {AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnChanges, Output, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatTooltipModule} from '@angular/material/tooltip';
import {OnboardingGuide, OnboardingMilestone, OnboardingTask} from '@src/app/core/models/onboarding-guide.model';
import {OnboardingGuideService} from '@src/app/core/services/onboarding-guide.service';
import {finalize} from 'rxjs/operators';

@Component({
    selector: 'app-onboarding-guide',
    imports: [MatButtonModule, MatIconModule, MatProgressBarModule, MatTooltipModule],
    templateUrl: './onboarding-guide.component.html',
    styleUrls: ['./onboarding-guide.component.scss'],
})
export class OnboardingGuideComponent implements AfterViewInit, OnChanges {
    private readonly service = inject(OnboardingGuideService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly changes = inject(ChangeDetectorRef);
    private readonly snackBar = inject(MatSnackBar);
    private readonly dialogs = inject(MatDialog);
    private context: string | null = null;
    private viewReady = false;
    private readonly attempted = new Set<string>();
    private pendingPresentation: string | null = null;
    private interaction = 0;

    @ViewChild('launcher', {read: ElementRef}) private launcher?: ElementRef<HTMLButtonElement>;
    @ViewChild('panel') private panel?: ElementRef<HTMLElement>;
    @Output() public dismissed = new EventEmitter<void>();
    @Output() public navigationRequested = new EventEmitter<OnboardingTask>();
    @Output() public opened = new EventEmitter<void>();
    @Input() public obscured = false;
    public guide: OnboardingGuide | null = null;
    public expanded = false;
    public saving = false;
    public selected: OnboardingMilestone | null = null;

    public get available(): boolean { return !!(this.guide?.tasks.length || this.guide?.message); }
    public get visible(): boolean { return this.available && !this.guide.hidden; }
    public get selectedTask(): OnboardingTask | undefined { return this.guide?.tasks.find(task => task.key === this.selected); }

    public constructor() {
        this.service.guide$.pipe(takeUntilDestroyed()).subscribe(guide => {
            if (guide) {
                const context = `${guide.tenant_id}:${guide.user_id}`;
                if (context !== this.context) {
                    this.expanded = false;
                    this.selected = null;
                    this.context = context;
                }
                if (!guide.tasks.some(task => task.key === this.selected)) this.selected = guide.next_task ?? guide.tasks[0]?.key ?? null;
                if (guide.hidden) this.expanded = false;
            }
            this.guide = guide;
            this.scheduleAutoOpen();
        });
        this.dialogs.afterAllClosed.pipe(takeUntilDestroyed()).subscribe(() => this.scheduleAutoOpen());
    }

    public ngAfterViewInit(): void {
        this.viewReady = true;
        this.scheduleAutoOpen();
    }

    public ngOnChanges(): void { this.scheduleAutoOpen(); }

    // Run after the layout has mounted and opened any mandatory setup dialog.
    private scheduleAutoOpen(): void {
        queueMicrotask(() => {
            if (this.destroyRef.destroyed || !this.viewReady || this.obscured || this.dialogs.openDialogs.length || !this.visible || this.expanded || this.saving) return;
            if (this.pendingPresentation === this.context) {
                this.pendingPresentation = null;
                this.selected = this.guide.next_task ?? this.guide.tasks[0]?.key ?? null;
                this.expand(false);
                return;
            }
            if (!this.guide.auto_open || this.attempted.has(this.context)) return;
            const context = this.context;
            const interaction = this.interaction;
            this.attempted.add(context);
            this.service.present().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: ({guide, claimed}) => {
                    if (!guide || context !== this.context || interaction !== this.interaction) return;
                    if (this.guide) this.guide = guide;
                    if (claimed) this.pendingPresentation = context;
                    this.scheduleAutoOpen();
                },
                // Optional guidance must not interrupt work when persistence is unavailable.
                error: () => {},
            });
        });
    }

    private expand(focus: boolean): void {
        this.opened.emit();
        this.expanded = true;
        this.changes.detectChanges();
        if (focus) this.panel?.nativeElement.focus();
    }

    public selectTask(task: OnboardingTask): void {
        if (this.saving) return;
        this.selected = task.key;
        this.navigationRequested.emit(task);
    }

    public actionLabel(task: OnboardingTask): string {
        return {site: 'Create site', forms: 'Choose forms', structure: 'Prepare floor', inspection: 'Record inspection', report: task.action.path === '/app/reports' ? 'View reports' : 'Create report'}[task.key];
    }

    public open(): void {
        if (!this.available || this.saving) return;
        this.interaction++;
        this.pendingPresentation = null;
        if (this.guide.hidden) {
            this.saveHidden(false);
        } else {
            this.expand(true);
            if (this.guide.presented === false) {
                const context = this.context;
                const interaction = this.interaction;
                this.attempted.add(context);
                this.service.present().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                    next: ({guide}) => { if (guide && this.guide && context === this.context && interaction === this.interaction) this.guide = guide; },
                    error: () => this.snackBar.open('Couldn’t save your guide preference. Please try again.', 'Close', {duration: 5000}),
                });
            }
        }
    }

    public minimise(restoreFocus = true): void {
        this.interaction++;
        this.pendingPresentation = null;
        this.expanded = false;
        if (this.guide?.presented === false && !this.attempted.has(this.context)) {
            this.attempted.add(this.context);
            this.service.present().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({error: () => {}});
        }
        this.changes.detectChanges();
        if (restoreFocus) this.launcher?.nativeElement.focus();
    }

    public hide(): void {
        this.interaction++;
        this.pendingPresentation = null;
        this.saveHidden(true);
    }

    private saveHidden(hidden: boolean): void {
        if (this.saving) return;
        this.saving = true;
        const context = this.context;
        this.service.setHidden(hidden).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.saving = false)).subscribe({
            next: guide => {
                if (!guide || context !== this.context) return;
                this.guide = guide;
                this.saving = false;
                if (hidden) {
                    this.expanded = false;
                    this.dismissed.emit();
                } else {
                    this.open();
                }
            },
            error: () => this.snackBar.open('Couldn’t save your guide preference. Please try again.', 'Close', {duration: 5000}),
        });
    }
}
